import {
  formatMicroUsdToUsd,
  makeColumnsForUnifiedCredits,
} from "@app/components/poke/credits/columns";
import { PokeDataTableConditionalFetch } from "@app/components/poke/PokeConditionalDataTables";
import type { PokeStripeSubscriptionWire } from "@app/lib/api/poke/workspace_info";
import { safeLazy, Tooltip } from "@dust-tt/sparkle";
import { Suspense } from "react";

const PokeProgrammaticCostChart = safeLazy(() =>
  import("@app/components/poke/credits/PokeProgrammaticCostChart").then(
    (mod) => ({
      default: mod.PokeProgrammaticCostChart,
    })
  )
);

const PokeAwuUsageFromAnalyticsChart = safeLazy(() =>
  import("@app/components/poke/credits/PokeAwuUsageFromAnalyticsChart").then(
    (mod) => ({ default: mod.PokeAwuUsageFromAnalyticsChart })
  )
);

function PokeChartFallback() {
  return (
    <div className="h-96 animate-pulse rounded-lg bg-muted-background dark:bg-muted-background-night" />
  );
}

import { PokeDataTable } from "@app/components/poke/shadcn/ui/data_table";
import type { PokeCreditsData } from "@app/poke/swr/credits";
import { usePokeCredits } from "@app/poke/swr/credits";
import type { PokeUnifiedCreditRow } from "@app/types/api/poke/credits";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";

const ONE_DOLLAR_MICRO_USD = 1_000_000;

interface CreditsDataTableProps {
  owner: WorkspaceType;
  subscription: SubscriptionType;
  stripeSubscription: PokeStripeSubscriptionWire | null;
  loadOnInit?: boolean;
}

function sortRowsByStartDateDescending(
  rows: PokeUnifiedCreditRow[]
): PokeUnifiedCreditRow[] {
  return [...rows].sort((a, b) => {
    const aStart =
      (a.internal?.startDate
        ? new Date(a.internal.startDate).getTime()
        : a.metronome?.startDate) ?? null;
    const bStart =
      (b.internal?.startDate
        ? new Date(b.internal.startDate).getTime()
        : b.metronome?.startDate) ?? null;
    // Null start dates go first (pending credits).
    if (aStart === null && bStart === null) {
      return 0;
    }
    if (aStart === null) {
      return -1;
    }
    if (bStart === null) {
      return 1;
    }
    return bStart - aStart;
  });
}

export function CreditsDataTable({
  owner,
  subscription,
  stripeSubscription,
  loadOnInit,
}: CreditsDataTableProps) {
  // Get the billing cycle start day from Stripe subscription, fallback to Dust subscription
  const getBillingCycleStartDay = (): number | null => {
    if (stripeSubscription?.current_period_start) {
      return new Date(stripeSubscription.current_period_start * 1000).getDate();
    }
    if (subscription.startDate) {
      return new Date(subscription.startDate).getDate();
    }
    return null;
  };
  const billingCycleStartDay = getBillingCycleStartDay();

  return (
    <>
      <PokeDataTableConditionalFetch<PokeCreditsData, PokeCreditsData>
        header="API Usage"
        owner={owner}
        loadOnInit={loadOnInit}
        useSWRHook={usePokeCredits}
      >
        {(data) => (
          <div className="space-y-4">
            {data.excessCreditsLast30DaysMicroUsd > ONE_DOLLAR_MICRO_USD && (
              <div className="rounded-md border border-warning-200 bg-warning-50 p-3 dark:border-warning-200-night dark:bg-warning-50-night">
                <Tooltip
                  label="Excess credits are created when programmatic usage exceeds available credits. This tracks over-consumption that needs to be billed."
                  trigger={
                    <p className="cursor-help text-sm font-medium text-warning-800 dark:text-warning-800-night">
                      Excess credits (last 30 days):{" "}
                      {formatMicroUsdToUsd(
                        data.excessCreditsLast30DaysMicroUsd
                      )}
                    </p>
                  }
                />
              </div>
            )}
            <PokeDataTable
              columns={makeColumnsForUnifiedCredits({
                metronomeCustomerId: owner.metronomeCustomerId,
                owner,
              })}
              data={sortRowsByStartDateDescending(data.rows)}
              defaultFilterColumn="type"
            />
          </div>
        )}
      </PokeDataTableConditionalFetch>

      {billingCycleStartDay && (
        <div className="flex flex-col gap-4">
          <Suspense fallback={<PokeChartFallback />}>
            <PokeProgrammaticCostChart
              owner={owner}
              billingCycleStartDay={billingCycleStartDay}
            />
          </Suspense>
          <Suspense fallback={<PokeChartFallback />}>
            <PokeAwuUsageFromAnalyticsChart
              owner={owner}
              billingCycleStartDay={billingCycleStartDay}
            />
          </Suspense>
        </div>
      )}
    </>
  );
}
